import { flatten, omit, pick, snakeCase } from 'lodash';

import type CarrierAccount from '~/types/CarrierAccount';
import type Rate from '~/types/Rate';

import { type OrderObjectId, type OrderSelection } from '~/types/Order';
import { type RateDisplayAttribute } from '~/types/Rate';
import { track } from '~/utils/segment';

import { trackingHelper } from '.';
import {
  ADDRESS_TRACKING_EVENT,
  API_TOKEN_UPDATED,
  BULK_BAR_TRACKING_EVENT,
  CARRIER_ACCOUNT_TRACKING_EVENT,
  DEFAULT_LABEL_SETTINGS_UPDATED_EVENT,
  DEFAULT_UPDATED_EVENT,
  IBD_MODAL_TRACKING_EVENT,
  INSURANCE_UPSELL_EVENT,
  ITEM_UPDATED_EVENT,
  LABEL_PURCHASED_EVENT,
  LABEL_UPDATED_EVENT,
  MANIFEST_TRACKING_EVENT,
  ORDER_TRACKING_EVENT,
  PACKAGE_TRACKING_EVENT,
  PARCEL_BOT_TRACKING_EVENT,
  PAYMENT_METHOD_EVENT,
  RATE_DISPLAYED_EVENT,
  RECIPIENT_ADDRESS_BOOK_QUERIED,
  RECIPIENT_ADDRESS_BOOK_UPDATED,
  STORE_CONNECTION_SETTINGS,
  type AllTrackedProps,
  type LabelDownloadedTrackingType,
  type OperationsForEvent,
  type RequiredPropsForOperation,
  type TrackedOrderDetails,
  type TrackingAppLocation,
  type TrackingEventName,
  type TrackingReference,
} from './types';

type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${CamelToSnakeCase<U>}`
  : S;

type KeysToSnakeCase<T> = {
  [K in keyof T as CamelToSnakeCase<K & string>]: T[K];
};

// type SnakeCaseProperties = KeysToSnakeCase<ReturnType<typeof trackingHelper['properties']>>;

export const objectToSnake = <T extends object>(input: T): KeysToSnakeCase<T> =>
  Object.entries(input).reduce<KeysToSnakeCase<T>>((accum, curr) => {
    accum[snakeCase(curr[0])] = curr[1];
    return accum;
  }, {} as KeysToSnakeCase<T>);

export const trackWithGlobalProps = <T extends object>(
  appLocation: TrackingAppLocation,
  event: string,
  input?: T,
) => {
  const properties = omitNestedObjects({
    ...trackingHelper.appendGlobalProps(input),
    appLocation,
  });
  const propertiesAsSnakeCase = objectToSnake(properties);

  track(event, propertiesAsSnakeCase);
};

export const getShipmentIdsByOrderId = (
  trackingReference: TrackingReference,
  orderId: OrderObjectId,
): number[] => {
  if (trackingReference.selectedOrders) return trackingReference.selectedOrders[orderId] || [];

  return [];
};

type TrackedShipmentProps = {
  batchCount: number;
  orderId: string;
  orderProcessingId: number;
} & TrackedOrderDetails;

const getShipmentProps = (
  orderId: string,
  orderProcessingId: number,
  batchCount: number,
  trackedOrderDetailsByShipment?: Record<number, TrackedOrderDetails>,
): TrackedShipmentProps => {
  if (!trackedOrderDetailsByShipment) {
    return {
      batchCount,
      orderId,
      orderProcessingId,
    };
  }

  const trackedOrderDetails = trackedOrderDetailsByShipment[orderProcessingId];
  return {
    batchCount,
    orderId,
    orderProcessingId,
    ...trackedOrderDetails,
  };
};

export const getOrdersPropsSet = (
  trackingReference: TrackingReference,
): Partial<AllTrackedProps>[] => {
  if (trackingReference.orderIds && trackingReference.orderIds?.length > 0) {
    const shipmentIdsAppliedToOrderIds = trackingReference.orderIds.map((orderId) => {
      const shipmentIds = getShipmentIdsByOrderId(trackingReference, orderId);
      if (shipmentIds.length === 0) return [{ orderId }];

      const batchCount = shipmentIds.length;
      return shipmentIds.map((shipmentId) =>
        getShipmentProps(
          orderId,
          shipmentId,
          batchCount,
          trackingReference.trackedOrderDetailsByShipment,
        ),
      );
    });

    return flatten(shipmentIdsAppliedToOrderIds);
  }

  if (trackingReference.orderId) {
    const orderId = trackingReference.orderId;
    const shipmentIds = getShipmentIdsByOrderId(trackingReference, orderId);
    const batchCount = shipmentIds.length;
    if (batchCount === 0) {
      return [{ orderId }];
    } else {
      return shipmentIds.map((shipmentId) =>
        getShipmentProps(
          orderId,
          shipmentId,
          batchCount,
          trackingReference.trackedOrderDetailsByShipment,
        ),
      );
    }
  }

  return [];
};

export const getAdditionalPropsSet = (
  trackingReference: TrackingReference,
): Partial<AllTrackedProps>[] => {
  const ordersPropsSet = getOrdersPropsSet(trackingReference);
  return ordersPropsSet.map((orderProps) => ({
    ...orderProps,
  }));
};

export const isNestedObject = (values: boolean | null | number | object | string | undefined) =>
  !!values && !!Object.values(values).find((v) => typeof v === 'object');

export const omitNestedObjects = (values: object) =>
  pick(
    values,
    Object.keys(values).filter((key) => !isNestedObject(values[key])),
  );

export const applyTransformsForTrackedProps = <P>(
  trackingReference: TrackingReference,
  properties?: P,
): object[] => {
  const transmittedTrackingReference = omit(
    trackingReference,
    'selectedOrders',
    'trackedOrderDetailsByShipment',
    'trackedUserSettings',
    'userDetails',
  );

  const referencePropertiesAsSnakeCase = objectToSnake(transmittedTrackingReference);
  const propertiesAsSnakeCase = objectToSnake(properties || {});

  const additionalPropsSet = getAdditionalPropsSet(trackingReference);
  if (additionalPropsSet.length > 0) {
    return additionalPropsSet.map((additionalProps) => {
      const additionalPropsAsSnakeCase = objectToSnake(additionalProps);
      return omitNestedObjects({
        ...referencePropertiesAsSnakeCase,
        ...additionalPropsAsSnakeCase,
        ...propertiesAsSnakeCase,
      });
    });
  }

  return [
    omitNestedObjects({
      ...referencePropertiesAsSnakeCase,
      ...propertiesAsSnakeCase,
    }),
  ];
};

export const labelPurchasedEvent = <N extends TrackingEventName>(
  eventName: N,
  operation: OperationsForEvent<N>,
  propsSet: Record<string, boolean | string>[],
) => {
  if (
    (eventName === LABEL_PURCHASED_EVENT.name &&
      operation === LABEL_PURCHASED_EVENT.operations.SAVE_ATTEMPTED) ||
    operation === LABEL_PURCHASED_EVENT.operations.TRANSACTION_COMPLETED
  ) {
    return (
      propsSet
        .map((set, idx) => (set.rate_ids ? { ...set, rate_id: set.rate_ids[idx] } : { ...set }))
        // This is a special case. If it's called as first transaction completed event
        // then we add isFirstLabelPurchase true to only the first event.
        .map((set, idx) => {
          if (operation === LABEL_PURCHASED_EVENT.operations.TRANSACTION_COMPLETED) {
            if (!idx && set['is_first_label_purchase'])
              return { ...set, is_first_label_purchase: true };

            return { ...set, is_first_label_purchase: false };
          }
          return set;
        })
    );
  }
  return propsSet;
};

/**
 * @description add custom properties by targeting event name. this can be helpful when adding properties to individual orders in an array (propsSet).
 */
const addCustomPropsByEventName = <N extends TrackingEventName>(
  eventName: N,
  operation: OperationsForEvent<N>,
  propsSet: Record<string, boolean | string>[],
) => {
  switch (eventName) {
    case LABEL_PURCHASED_EVENT.name:
      return labelPurchasedEvent(eventName, operation, propsSet);
    default:
      return propsSet;
  }
};

export const advancedTrack = <N extends TrackingEventName, P>(
  eventName: N,
  operation: OperationsForEvent<N>,
  trackingReference: TrackingReference,
  properties?: P,
): void => {
  // propsSet length is defined by the number of orderIds in trackingReference, each propsSet will fire a tracking event.
  let propsSet = applyTransformsForTrackedProps(trackingReference, properties) as Record<
    string,
    boolean | string
  >[];
  propsSet = addCustomPropsByEventName(eventName, operation, propsSet);

  propsSet.forEach((props) => {
    track(eventName, {
      ...props,
      operation,
    });
  });

  try {
    if (
      !!trackingHelper.fullStoryEvent &&
      eventName === ORDER_TRACKING_EVENT.name &&
      operation === ORDER_TRACKING_EVENT.operations.VIEWED
    ) {
      trackingHelper.fullStoryEvent(
        `${ORDER_TRACKING_EVENT.name} ${ORDER_TRACKING_EVENT.operations.VIEWED}`,
        {
          order_id: trackingReference.orderId || '',
          order_ids: trackingReference.orderIds || [],
          reference_id: trackingReference.referenceId,
        },
      );
    }
  } catch {
    // ignore it
  }
};

export const trackingFunctionFactory =
  <E extends TrackingEventName>(trackingEventName: E) =>
  <T extends TrackingReference, O extends OperationsForEvent<E>, P extends object>(
    trackingReference: T,
    operation: O,
    additionalProps: RequiredPropsForOperation<E, O> extends never
      ? P
      : P & RequiredPropsForOperation<E, O>,
  ) => {
    advancedTrack(trackingEventName, operation, trackingReference, additionalProps);
  };

export const trackCarrierAccountUpdate = trackingFunctionFactory(
  CARRIER_ACCOUNT_TRACKING_EVENT.name,
);
// @NOTE Event is disabled for now to lower usage.
export const trackOrderUpdate = (_1, _2, _3) => {}; // trackingFunctionFactory(ORDER_TRACKING_EVENT.name);
export const trackAddressUpdate = trackingFunctionFactory(ADDRESS_TRACKING_EVENT.name);
export const trackLabelPurchased = trackingFunctionFactory(LABEL_PURCHASED_EVENT.name);
export const trackPaymentMethodUpdate = trackingFunctionFactory(PAYMENT_METHOD_EVENT.name);
export const trackPackageUpdate = trackingFunctionFactory(PACKAGE_TRACKING_EVENT.name);
// @NOTE Event is disabled for now to lower usage.
export const trackRateUpdate = (_1, _2, _3) => {}; // trackingFunctionFactory(RATES_UPDATED_EVENT.name);
export const trackRateDisplay = trackingFunctionFactory(RATE_DISPLAYED_EVENT.name);
export const trackItemUpdated = trackingFunctionFactory(ITEM_UPDATED_EVENT.name);
export const trackManifestUpdated = trackingFunctionFactory(MANIFEST_TRACKING_EVENT.name);
export const trackLabelUpdated = trackingFunctionFactory(LABEL_UPDATED_EVENT.name);
export const trackParcelBotUpdated = trackingFunctionFactory(PARCEL_BOT_TRACKING_EVENT.name);
export const trackDefaultUpdated = trackingFunctionFactory(DEFAULT_UPDATED_EVENT.name);
export const trackDefaultLabelSettingsUpdated = trackingFunctionFactory(
  DEFAULT_LABEL_SETTINGS_UPDATED_EVENT.name,
);
export const trackBulkBar = trackingFunctionFactory(BULK_BAR_TRACKING_EVENT.name);
export const trackRecipientAddressBookUpdated = trackingFunctionFactory(
  RECIPIENT_ADDRESS_BOOK_UPDATED.name,
);
export const trackRecipientAddressBookQueried = trackingFunctionFactory(
  RECIPIENT_ADDRESS_BOOK_QUERIED.name,
);
export const trackAPITokenUpdated = trackingFunctionFactory(API_TOKEN_UPDATED.name);
export const trackStoreConnectionSettings = trackingFunctionFactory(STORE_CONNECTION_SETTINGS.name);
export const trackUpSellInsurance = trackingFunctionFactory(INSURANCE_UPSELL_EVENT.name);
export const trackIBDModal = trackingFunctionFactory(IBD_MODAL_TRACKING_EVENT.name);

export const trackDownload = (
  appLocation: TrackingAppLocation,
  event: LabelDownloadedTrackingType,
  orderSelection: OrderSelection,
) => {
  const trackingReference = trackingHelper.getTrackingReference(
    LABEL_UPDATED_EVENT.name,
    appLocation,
    { type: event },
  );

  const trackingReferenceWithOrderIds = trackingHelper.amendTrackingReferenceOrderSelection(
    trackingReference,
    orderSelection,
  );

  trackLabelUpdated(trackingReferenceWithOrderIds, LABEL_UPDATED_EVENT.operations.DOWNLOADED, {});
};

export type RateDisplayedEventDataType = {
  carrier: string;
  carrier_account_id?: string;
  price: string;
  rank: number;
  rate_id: string;
  service_level: string;
  tag: string;
  transit_time_in_days: number;
};

export const getRateDisplayedEventData = (
  rate: Rate,
  sortedRates: {
    displayAttribute: RateDisplayAttribute;
    rates: Rate[];
  }[],
  carrierAccounts: CarrierAccount[],
): RateDisplayedEventDataType => {
  const {
    amount,
    carrierAccount,
    estimatedDays,
    objectId,
    provider,
    servicelevel: { name: serviceLevelName },
  } = rate;

  const { accountId } =
    carrierAccounts.find((account) => account.objectId === carrierAccount) || {};

  const rateIds = sortedRates.map((entry) => entry.rates.map((rate) => rate.objectId)).flat();
  const rank = rateIds.indexOf(objectId) + 1;

  const tag =
    sortedRates.find((entry) => entry.rates.map((rate) => rate.objectId).includes(objectId))
      ?.displayAttribute || '';

  return {
    carrier: provider,
    carrier_account_id: accountId,
    price: amount,
    rank,
    rate_id: objectId,
    service_level: serviceLevelName,
    tag,
    transit_time_in_days: estimatedDays,
  };
};
